import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva } from 'class-variance-authority'
import { motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import styles from './styles/button.module.css' // Ensure this path is correct

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground shadow hover:bg-primary/90',
        destructive: 'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90',
        outline: 'border border-input bg-background shadow-sm hover:bg-accent/40 hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:bg-gray-100',
        link: 'text-primary underline-offset-4 hover:underline',
        gradient: styles.btnGradient,
        gradientPrimary:
          'relative overflow-hidden whitespace-nowrap text-white before:absolute before:inset-0 before:bg-gradient-to-r before:from-[#e61a27] before:via-[#9b1c76] before:to-[#5c2d91] before:bg-[length:200%_100%] before:animate-gradient-x',
        gradientSecondary:
          'relative overflow-hidden whitespace-nowrap text-white before:absolute before:inset-0 before:bg-gradient-to-r before:from-[#5c2d91] before:via-[#9b1c76] before:to-[#e61a27] before:bg-[length:200%_100%] before:animate-gradient-x',
        gradientAccent:
          'relative overflow-hidden whitespace-nowrap text-white before:absolute before:inset-0 before:bg-gradient-to-r before:from-[#ff9500] before:via-[#ff5e3a] before:to-[#e61a27] before:bg-[length:200%_100%] before:animate-gradient-x',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-10 rounded-md px-8',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, animate = true, ...props }, ref) => {
  const Comp = asChild ? Slot : 'button'
  const isGradient = variant === 'gradient'
  const isAnimatedGradient = ['gradientPrimary', 'gradientSecondary', 'gradientAccent'].includes(variant)

  // For standard button rendering
  if (!isAnimatedGradient) {
    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size, className }),
          isGradient && animate && styles.gradientAnimate,
          isGradient && styles.vibrantGlow
        )}
        ref={ref}
        {...props}
      >
        {props.children}
      </Comp>
    )
  }

  // For animated gradient button rendering using framer motion
  return (
    <motion.button
      className={cn(
        buttonVariants({ variant, size, className }),
        'shadow-md hover:shadow-lg transition-all duration-300',
        'after:absolute after:inset-0 after:opacity-0 after:bg-white after:mix-blend-overlay after:transition-opacity hover:after:opacity-10'
      )}
      whileHover={{
        scale: 1.03,
        boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      }}
      whileTap={{ scale: 0.97 }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
      ref={ref}
      {...props}
    >
      <span className="relative z-10">{props.children}</span>
    </motion.button>
  )
})

Button.displayName = 'Button'

export { Button, buttonVariants }
